import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import IconStarFill from "../../../components/icons/icon-star-fill";
import IconStarLine from "../../../components/icons/icon-star-line";
import IconHeart from "../../../components/icons/icon-heart";
import IconChatBubbleSquare from "../../../components/icons/icon-chat-bubble-square";
import IconAnonymous from "../../../components/icons/icon-anonymous";
import { useReviews } from "../hooks/use-reviews";
import { useAuth } from "../../auth/hooks/use-auth";
import { useTags } from "../../tag/hooks/use-tags";
import { TAG_COLORS } from "../../tag/colors";
import { reviewUploadsApi } from "../services/review-uploads-api";
import type { ReviewResponse } from "../types";
import AudioPlayer from "@/components/ui/audio-player";
import Input from "@/components/ui/input";
import Textarea from "@/components/ui/textarea";
import Button from "@/components/ui/button";
import Container from "@/components/ui/container";
import TagSelector from "@/components/ui/tag-selector";
import ImageUploadField from "@/components/ui/image-upload-field";
import FormAnswersSection from "./form-answers-section";
import VideoModal from "./video-modal";
import { useDirtyState } from "@/modules/form/components/form-builder/hooks/use-dirty-state";

const reviewSchema = z.object({
	// Rating & Classification
	rating: z.number().min(1, "Please select a rating").max(5),
	ratingNotApplicable: z.boolean(),
	type: z.enum(["testimonial", "feedback"]).nullable(),
	// Editable fields
	contentText: z.string(),
	customerName: z.string().min(1, "Customer name is required"),
	customerHeadline: z.string().optional(),
	customerEmail: z.string().email("Please enter a valid email address").optional().or(z.literal("")),
	customerPhone: z
		.string()
		.regex(/^$|^[+]?[\d\s\-().]{7,}$/, "Please enter a valid phone number")
		.optional(),
	customerJobTitle: z.string().optional(),
	customerCompanyName: z.string().optional(),
	customerCompanyWebsiteUrl: z.string().optional(),
});

export type ReviewFormData = z.infer<typeof reviewSchema>;

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface FormSourceReviewFormProps {
	onSuccess: (review: ReviewResponse) => void;
	onCancel: () => void;
	editMode?: boolean;
	initialData?: ReviewResponse | null;
}

function StarRatingInput({ value, onChange, faded }: { value: number; onChange: (rating: number) => void; faded?: boolean }) {
	const [hoverValue, setHoverValue] = useState(0);

	const displayValue = hoverValue > 0 ? hoverValue : value;

	return (
		<div className={`flex items-center gap-1.5 transition-opacity ${faded ? "opacity-50" : ""}`} onMouseLeave={() => setHoverValue(0)}>
			{[1, 2, 3, 4, 5].map((star) => (
				<button
					key={star}
					type="button"
					onClick={() => onChange(star)}
					onMouseEnter={() => setHoverValue(star)}
					className="p-0.5 transition-transform cursor-pointer hover:scale-110"
					aria-label={`Rate ${star} stars`}
				>
					{star <= displayValue ? (
						<IconStarFill className={`w-10 h-10 ${hoverValue > 0 && star <= hoverValue ? "text-brand/70" : "text-brand"}`} />
					) : (
						<IconStarLine className={`w-10 h-10 ${faded ? "text-gray-300" : "text-gray-300 hover:text-brand/50"}`} />
					)}
				</button>
			))}
		</div>
	);
}

// Helper to format date for display
function formatDate(dateStr: string | undefined): string {
	if (!dateStr) return "—";
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-US", {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

// Read-only field display component
function ReadOnlyField({ label, value, isLink = false }: { label: string; value: string | null | undefined; isLink?: boolean }) {
	return (
		<div className="space-y-1.5">
			<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">{label}</label>
			{isLink && value ? (
				<a href={value} target="_blank" rel="noopener noreferrer" className="text-sm text-brand hover:underline flex items-center gap-1">
					{value}
					<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
					</svg>
				</a>
			) : (
				<div className="text-sm text-primary">{value || "—"}</div>
			)}
		</div>
	);
}

export default function FormSourceReviewForm({ onCancel, editMode = false, initialData = null }: FormSourceReviewFormProps) {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
	const [videoModalOpen, setVideoModalOpen] = useState(false);
	const savedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Track sidebar collapsed state for sticky footer positioning
	const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
		const saved = localStorage.getItem("sidebar-collapsed");
		return saved ? JSON.parse(saved) : false;
	});

	// Listen for sidebar collapse changes
	useEffect(() => {
		const handleSidebarChange = (e: CustomEvent<{ isCollapsed: boolean }>) => {
			setIsSidebarCollapsed(e.detail.isCollapsed);
		};

		window.addEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);

		return () => {
			window.removeEventListener("sidebar-collapse-change", handleSidebarChange as EventListener);
		};
	}, []);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (savedTimeoutRef.current) {
				clearTimeout(savedTimeoutRef.current);
			}
		};
	}, []);

	const [selectedTagIds, setSelectedTagIds] = useState<string[]>(editMode && initialData?.tags ? initialData.tags.map((t) => t.id) : []);
	const [avatarImage, setAvatarImage] = useState<string | null>(editMode && initialData?.customer_avatar_url ? initialData.customer_avatar_url : null);
	const [companyLogoImage, setCompanyLogoImage] = useState<string | null>(editMode && initialData?.customer_company_logo_url ? initialData.customer_company_logo_url : null);
	const [isUploading, setIsUploading] = useState(false);
	const [tagsNextCursor, setTagsNextCursor] = useState<string | null>(null);
	const [isLoadingMoreTags, setIsLoadingMoreTags] = useState(false);

	const { currentWorkspaceId } = useAuth();
	const { updateReview } = useReviews();
	const { tags, indexTags, searchTags, createTag, getTagsByIds, mergeSelectedTags } = useTags();

	// Get initial selected tag IDs from initialData (stable reference using useMemo)
	const initialSelectedTagIds = useMemo(
		() => (editMode && initialData?.tags ? initialData.tags.map((t) => t.id) : []),
		// Only depend on initialData?.id to ensure stability - tags won't change for same review
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[initialData?.id],
	);

	// Load tags on mount
	useEffect(() => {
		if (!currentWorkspaceId) return;

		(async () => {
			const response = await indexTags(currentWorkspaceId);
			if (response) {
				setTagsNextCursor(response.meta.next_cursor);
			}
		})();
	}, [currentWorkspaceId, indexTags]);

	// Fetch and merge selected tags separately (only when we have initial selected tags)
	useEffect(() => {
		if (!currentWorkspaceId || initialSelectedTagIds.length === 0) return;

		(async () => {
			const selectedTags = await getTagsByIds(currentWorkspaceId, initialSelectedTagIds);
			if (selectedTags) {
				mergeSelectedTags(selectedTags);
			}
		})();
	}, [currentWorkspaceId, initialSelectedTagIds, getTagsByIds, mergeSelectedTags]);

	const handleLoadMoreTags = useCallback(async () => {
		if (!currentWorkspaceId || !tagsNextCursor || isLoadingMoreTags) return;

		setIsLoadingMoreTags(true);
		const response = await indexTags(currentWorkspaceId, tagsNextCursor);
		if (response) {
			setTagsNextCursor(response.meta.next_cursor);
		}
		setIsLoadingMoreTags(false);
	}, [currentWorkspaceId, tagsNextCursor, isLoadingMoreTags, indexTags]);

	// Handle searching tags
	const handleSearchTags = useCallback(
		async (query: string, cursor?: string | null) => {
			if (!currentWorkspaceId) return null;
			const response = await searchTags(currentWorkspaceId, query, cursor);
			if (response) {
				return {
					tags: response.data,
					hasMore: !!response.meta.next_cursor,
					nextCursor: response.meta.next_cursor,
				};
			}
			return null;
		},
		[currentWorkspaceId, searchTags],
	);

	// Handle creating a new tag
	const handleCreateTag = useCallback(
		(label: string, onCreated: (tagId: string) => void) => {
			if (!currentWorkspaceId) return;
			const randomColor = TAG_COLORS[Math.floor(Math.random() * TAG_COLORS.length)].value;
			createTag(currentWorkspaceId, { label, color: randomColor }).then((response) => {
				if (response.data?.id) {
					onCreated(response.data.id);
				}
			});
		},
		[currentWorkspaceId, createTag],
	);

	// Handle tag selection - merge into store so it remains visible
	const handleTagSelect = useCallback(
		(tag: { id: string; label: string; color: string }) => {
			mergeSelectedTags([tag]);
		},
		[mergeSelectedTags],
	);

	const {
		register,
		handleSubmit,
		setValue,
		watch,
		trigger,
		reset,
		formState: { errors, isValid, isDirty },
	} = useForm<ReviewFormData>({
		resolver: zodResolver(reviewSchema),
		mode: "onChange",
		defaultValues:
			editMode && initialData
				? {
						// Rating & Classification
						rating: initialData.rating || 1,
						ratingNotApplicable: !initialData.rating,
						type: (initialData.type as "testimonial" | "feedback") || null,
						// Editable fields
						contentText: initialData.content_text || "",
						customerName: initialData.customer_name || "",
						customerHeadline: initialData.customer_headline || "",
						customerEmail: initialData.customer_email || "",
						customerPhone: initialData.customer_phone || "",
						customerJobTitle: initialData.customer_job_title || "",
						customerCompanyName: initialData.customer_company_name || "",
						customerCompanyWebsiteUrl: initialData.customer_company_website_url || "",
					}
				: {
						// Default values for create mode
						rating: 1,
						ratingNotApplicable: false,
						type: null,
						contentText: "",
						customerName: "",
						customerHeadline: "",
						customerEmail: "",
						customerPhone: "",
						customerJobTitle: "",
						customerCompanyName: "",
						customerCompanyWebsiteUrl: "",
					},
	});

	const ratingValue = watch("rating");
	const ratingNotApplicable = watch("ratingNotApplicable");

	// Add hasChanges logic
	const hasNonFormChanges =
		editMode && initialData
			? JSON.stringify(selectedTagIds.sort()) !== JSON.stringify((initialData.tags || []).map((t) => t.id).sort()) ||
				(avatarImage && avatarImage.startsWith("data:")) ||
				(companyLogoImage && companyLogoImage.startsWith("data:"))
			: false;

	const hasChanges = !editMode || isDirty || hasNonFormChanges;

	// Set up dirty state tracking and navigation guards
	useDirtyState({ hasUnsavedChanges: !!hasChanges });

	// Add handleDiscard function
	const handleDiscard = () => {
		if (editMode && initialData) {
			reset();
			setSelectedTagIds(initialData.tags ? initialData.tags.map((t) => t.id) : []);
			setAvatarImage(initialData.customer_avatar_url || null);
			setCompanyLogoImage(initialData.customer_company_logo_url || null);
			setError(null);
		} else {
			onCancel();
		}
	};

	// Helper to convert base64 data URL to Blob
	function dataUrlToBlob(dataUrl: string): Blob {
		const arr = dataUrl.split(",");
		const mime = arr[0].match(/:(.*?);/)?.[1] || "image/png";
		const bstr = atob(arr[1]);
		let n = bstr.length;
		const u8arr = new Uint8Array(n);
		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}
		return new Blob([u8arr], { type: mime });
	}

	const onSubmit = async (data: ReviewFormData) => {
		if (!currentWorkspaceId || !initialData?.id) {
			setError("Workspace or review not found. Please refresh the page.");
			return;
		}

		// Clear any existing timeout
		if (savedTimeoutRef.current) {
			clearTimeout(savedTimeoutRef.current);
		}

		setIsLoading(true);
		setError(null);
		setSaveStatus("saving");

		try {
			// Build the API request - update editable fields
			// Note: type is not sent - backend determines it based on rating
			const reviewRequest = {
				rating: data.rating,
				content_text: data.contentText,
				customer_name: data.customerName,
				customer_headline: data.customerHeadline || undefined,
				customer_email: data.customerEmail || undefined,
				customer_phone: data.customerPhone || undefined,
				customer_job_title: data.customerJobTitle || undefined,
				customer_company_name: data.customerCompanyName || undefined,
				customer_company_website_url: data.customerCompanyWebsiteUrl || undefined,
				tag_ids: selectedTagIds,
			};

			// Update the review
			const result = await updateReview(currentWorkspaceId, initialData.id, reviewRequest);

			if (!result) {
				setError("Failed to update review. Please try again.");
				setIsLoading(false);
				return;
			}

			// Step 2: Upload new images if present
			const isNewImage = (img: string | null) => img && img.startsWith("data:");
			const hasImagesToUpload = isNewImage(avatarImage) || isNewImage(companyLogoImage);

			if (hasImagesToUpload) {
				setIsLoading(false);
				setIsUploading(true);

				const uploadPromises: Promise<void>[] = [];

				// Avatar upload (only new base64 images)
				if (isNewImage(avatarImage) && avatarImage) {
					uploadPromises.push(
						(async () => {
							try {
								const blob = dataUrlToBlob(avatarImage);
								const file = new File([blob], "avatar.png", { type: blob.type });
								await reviewUploadsApi.uploadAvatar(currentWorkspaceId, result.id, file);
							} catch (err) {
								console.error("Avatar upload failed:", err);
							}
						})(),
					);
				}

				// Company logo upload (only new base64 images)
				if (isNewImage(companyLogoImage) && companyLogoImage) {
					uploadPromises.push(
						(async () => {
							try {
								const blob = dataUrlToBlob(companyLogoImage);
								const file = new File([blob], "logo.png", { type: blob.type });
								await reviewUploadsApi.uploadCompanyLogo(currentWorkspaceId, result.id, file);
							} catch (err) {
								console.error("Company logo upload failed:", err);
							}
						})(),
					);
				}

				// Wait for all uploads to complete
				await Promise.all(uploadPromises);

				// Small delay to show completion state
				await new Promise((resolve) => setTimeout(resolve, 500));

				setIsUploading(false);
			}

			// Update form with new values and reset dirty state
			reset({
				rating: result.rating || 1,
				ratingNotApplicable: !result.rating,
				type: (result.type as "testimonial" | "feedback") || null,
				contentText: result.content_text || "",
				customerName: result.customer_name || "",
				customerHeadline: result.customer_headline || "",
				customerEmail: result.customer_email || "",
				customerPhone: result.customer_phone || "",
				customerJobTitle: result.customer_job_title || "",
				customerCompanyName: result.customer_company_name || "",
				customerCompanyWebsiteUrl: result.customer_company_website_url || "",
			});

			// Merge saved tags into store to ensure newly created tags are available
			// Use result.tags if available, otherwise keep current selection
			const savedTagIds = result.tags && result.tags.length > 0 ? result.tags.map((t) => t.id) : selectedTagIds;
			if (result.tags && result.tags.length > 0) {
				mergeSelectedTags(result.tags);
			}
			setSelectedTagIds(savedTagIds);

			// Update avatar and company logo with final URLs
			setAvatarImage(result.customer_avatar_url || null);
			setCompanyLogoImage(result.customer_company_logo_url || null);

			setSaveStatus("saved");
			setIsLoading(false);

			// Hide "Saved" after 2 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 2000);
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred while saving the review");
			setSaveStatus("error");
			setIsLoading(false);

			// Reset error status after 3 seconds
			savedTimeoutRef.current = setTimeout(() => {
				setSaveStatus("idle");
			}, 3000);
		}
	};

	if (!initialData) {
		return <div className="text-center py-12 text-tertiary">No review data available</div>;
	}

	const contentType = initialData.content_type || "text";

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="space-y-8">
				{/* Rating & Classification Section */}
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Rating & Classification</h3>
							<p className="text-t-13 text-tertiary mt-0.5">Select a star rating or manually classify the review</p>
						</div>
					</div>

					{/* Content */}
					<div className="flex border-t border-gray-200">
						{/* Left Column - Star Rating */}
						<div className="p-6 flex items-center">
							<StarRatingInput
								value={ratingNotApplicable ? 0 : (ratingValue ?? 0)}
								onChange={(rating) => {
									setValue("rating", rating, { shouldDirty: true });
									setValue("ratingNotApplicable", false, { shouldDirty: true });
									setValue("type", null, { shouldDirty: true });
									trigger("rating");
								}}
								faded={ratingNotApplicable}
							/>
						</div>

						{/* Divider */}
						<div className="w-px bg-gray-200" />

						{/* Right Column - Manual Classification (Read Only) */}
						<div className="flex-1 p-6 transition-opacity flex items-center opacity-60">
							<div className="flex gap-3 w-full">
								{(() => {
									const isManualTestimonial = ratingNotApplicable && watch("type") === "testimonial";
									const isAutoTestimonial = !ratingNotApplicable && ratingValue !== null && ratingValue >= 4;
									const isTestimonialHighlighted = isManualTestimonial || isAutoTestimonial;

									return (
										<button
											type="button"
											disabled={true}
											className={`flex-1 px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 cursor-not-allowed ${
												isTestimonialHighlighted ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-secondary"
											}`}
										>
											<IconHeart className="w-5 h-5 flex-shrink-0" solid={isTestimonialHighlighted} />
											<div>
												<div className="font-medium text-sm">Testimonial</div>
												<div className="text-xs opacity-75">Positive feedback</div>
											</div>
										</button>
									);
								})()}
								{(() => {
									const isManualFeedback = ratingNotApplicable && watch("type") === "feedback";
									const isAutoFeedback = !ratingNotApplicable && ratingValue !== null && ratingValue > 0 && ratingValue < 4;
									const isFeedbackHighlighted = isManualFeedback || isAutoFeedback;

									return (
										<button
											type="button"
											disabled={true}
											className={`flex-1 px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 cursor-not-allowed ${
												isFeedbackHighlighted ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-secondary"
											}`}
										>
											<IconChatBubbleSquare className="w-5 h-5 flex-shrink-0" solid={isFeedbackHighlighted} />
											<div>
												<div className="font-medium text-sm">Feedback</div>
												<div className="text-xs opacity-75">Neutral or constructive</div>
											</div>
										</button>
									);
								})()}
							</div>
						</div>
					</div>

					{/* Error */}
					{errors.rating && <p className="text-xs text-red-500 px-6 pb-4">{errors.rating.message}</p>}
				</div>

				{/* Review Information Section */}
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Review Information</h3>
							<p className="text-t-13 text-tertiary mt-0.5">What they said about you</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6 space-y-5">
						{/* Video Player (for video reviews) - Read Only */}
						{contentType === "video" && initialData.mux_playback_id && (
							<div className="space-y-1.5">
								<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Video</label>
								<div className="rounded-lg overflow-hidden border border-gray-200 bg-gray-900">
									<div className="aspect-video relative">
										{initialData.video_thumbnail_url ? (
											<img src={initialData.video_thumbnail_url} alt="Video thumbnail" className="w-full h-full object-cover" />
										) : (
											<img src={`https://image.mux.com/${initialData.mux_playback_id}/thumbnail.jpg?time=0`} alt="Video thumbnail" className="w-full h-full object-cover" />
										)}
										<div className="absolute inset-0 flex items-center justify-center bg-black/30">
											<button
												type="button"
												onClick={() => setVideoModalOpen(true)}
												className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors cursor-pointer"
											>
												<svg className="w-8 h-8 text-gray-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
													<path d="M8 5v14l11-7z" />
												</svg>
											</button>
										</div>
									</div>
								</div>
								<VideoModal open={videoModalOpen} onOpenChange={setVideoModalOpen} playbackId={initialData.mux_playback_id} />
								<p className="text-xs text-tertiary mt-2">Video cannot be changed for form submissions.</p>
							</div>
						)}

						{/* Audio Player (for audio reviews) - Read Only */}
						{contentType === "audio" && initialData.audio_url && (
							<div className="space-y-1.5">
								<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Audio</label>
								<AudioPlayer src={initialData.audio_url} initialDuration={initialData.audio_duration} />
								<p className="text-xs text-tertiary mt-2">Audio cannot be changed for form submissions.</p>
							</div>
						)}

						{/* Content Text - Editable */}
						<Textarea
							label={contentType === "text" ? "Content" : "Excerpt"}
							rows={4}
							placeholder="Enter review content..."
							disabled={isLoading}
							error={errors.contentText?.message}
							{...register("contentText")}
						/>

						{/* Date - Read Only */}
						<ReadOnlyField label="Date" value={formatDate(initialData.date)} />

						{/* Source - Read Only */}
						<div className="space-y-1.5">
							<label className="block text-xs font-medium text-tertiary uppercase tracking-wide">Source</label>
							<div className="flex items-center gap-3">
								<span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-100 text-sm text-secondary">
									<span>📝</span>
									<span>Form Submission</span>
								</span>
								{initialData?.form ? (
									<a href={`/${initialData.workspace_id}/forms/${initialData.form.id}/edit`} className="text-sm text-brand hover:underline flex items-center gap-1">
										{initialData.form.title}
										<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
										</svg>
									</a>
								) : (
									<div className="text-sm text-brand flex items-center gap-1">
										{initialData.form_title} <span className="opacity-50">(deleted)</span>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				{/* Form Responses Section */}
				{initialData.answers && initialData.answers.length > 0 && <FormAnswersSection answers={initialData.answers} />}

				{/* Consent Section - Read Only */}
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Consent & Usage</h3>
							<p className="text-t-13 text-tertiary mt-0.5">How this review can be used</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6 opacity-60">
						<div className="flex flex-col gap-3">
							<button
								type="button"
								disabled={true}
								className={`px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 cursor-not-allowed ${
									initialData.consent === "public" ? "border-green-500 bg-green-50 text-green-700" : "border-gray-200 text-secondary"
								}`}
							>
								<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
									/>
								</svg>
								<div>
									<div className="font-medium text-sm">Share it publicly</div>
									<div className="text-xs opacity-75">On your website, social media, and marketing materials</div>
								</div>
							</button>
							<button
								type="button"
								disabled={true}
								className={`px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 cursor-not-allowed ${
									initialData.consent === "anonymous" ? "border-purple-500 bg-purple-50 text-purple-700" : "border-gray-200 text-secondary"
								}`}
							>
								<IconAnonymous className="w-5 h-5 shrink-0" />
								<div>
									<div className="font-medium text-sm">Share it anonymously</div>
									<div className="text-xs opacity-75">On your website, social media, and marketing materials</div>
								</div>
							</button>
							<button
								type="button"
								disabled={true}
								className={`px-4 py-3 rounded-lg border transition-all text-left flex items-center gap-3 cursor-not-allowed ${
									initialData.consent === "private" ? "border-blue-500 bg-blue-50 text-blue-700" : "border-gray-200 text-secondary"
								}`}
							>
								<svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
									/>
								</svg>
								<div>
									<div className="font-medium text-sm">Keep it private</div>
									<div className="text-xs opacity-75">For internal use and feedback only</div>
								</div>
							</button>
						</div>
						<p className="text-xs text-tertiary mt-3">Consent cannot be changed for reviews submitted via forms.</p>
					</div>
				</div>

				{/* Person Information Section - Editable */}
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Person Information</h3>
							<p className="text-t-13 text-tertiary mt-0.5">Who gave this review</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6 space-y-5">
						<Input label="Name" type="text" placeholder="e.g. John Smith" required disabled={isLoading} error={errors.customerName?.message} {...register("customerName")} />
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
							<Input label="Email" type="email" placeholder="e.g. john@company.com" disabled={isLoading} error={errors.customerEmail?.message} {...register("customerEmail")} />
							<Input label="Phone" type="tel" placeholder="e.g. +1 (555) 123-4567" disabled={isLoading} error={errors.customerPhone?.message} {...register("customerPhone")} />
						</div>
						<ImageUploadField label="Photo" value={avatarImage} onChange={setAvatarImage} aspectRatio={1} maxDimension={256} disabled={isLoading} className="max-w-32" />
					</div>
				</div>

				{/* Company Information Section - Editable */}
				<div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Company Information</h3>
							<p className="text-t-13 text-tertiary mt-0.5">Where do they work?</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6 space-y-5">
						<div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
							<Input label="Job Title" type="text" placeholder="e.g. Head of Marketing" disabled={isLoading} error={errors.customerJobTitle?.message} {...register("customerJobTitle")} />
							<Input label="Company" type="text" placeholder="e.g. Acme Inc." disabled={isLoading} error={errors.customerCompanyName?.message} {...register("customerCompanyName")} />
						</div>
						<Input
							label="Website"
							type="url"
							placeholder="e.g. https://acme.com"
							disabled={isLoading}
							error={errors.customerCompanyWebsiteUrl?.message}
							{...register("customerCompanyWebsiteUrl")}
						/>
						<ImageUploadField label="Logo" value={companyLogoImage} onChange={setCompanyLogoImage} maxDimension={256} disabled={isLoading} className="max-w-48" />
					</div>
				</div>

				{/* Tags Section */}
				<div className="bg-white border border-gray-200 rounded-xl">
					{/* Header */}
					<div className="flex items-start gap-3 p-6">
						<div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
							<svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
								/>
							</svg>
						</div>
						<div>
							<h3 className="text-t-15 font-semibold text-primary">Tags</h3>
							<p className="text-t-13 text-tertiary mt-0.5">Organize this review with tags for easy filtering</p>
						</div>
					</div>

					{/* Content */}
					<div className="border-t border-gray-200 p-6">
						<TagSelector
							tags={tags || []}
							selectedTagIds={selectedTagIds}
							onChange={setSelectedTagIds}
							disabled={isLoading}
							placeholder="Search and select tags..."
							onCreateTag={handleCreateTag}
							hasMore={!!tagsNextCursor}
							isLoadingMore={isLoadingMoreTags}
							onLoadMore={handleLoadMoreTags}
							onSearch={handleSearchTags}
							onTagSelect={handleTagSelect}
						/>
					</div>
				</div>

				{error && (
					<div className="bg-red-50 border border-red-200 rounded-md p-3">
						<p className="text-sm text-red-600">{error}</p>
					</div>
				)}

				{/* Spacer for sticky footer */}
				<div className="h-24" />
			</div>

			{/* Sticky footer with Save/Cancel buttons */}
			<div
				className={`fixed bottom-0 left-0 z-20 bg-white border-t border-gray-200 px-8 py-4 transition-all duration-300 ${isSidebarCollapsed ? "w-[calc(100%-64px)] ml-16" : "w-[calc(100%-256px)] ml-64"}`}
			>
				<Container>
					<div className="flex items-center justify-end">
						<div className="flex items-center gap-3">
							{/* Status indicator */}
							{saveStatus !== "idle" && (
								<span
									className={`text-sm font-medium flex items-center gap-1.5 transition-opacity duration-200 ${
										saveStatus === "saving" ? "text-gray-500" : saveStatus === "saved" ? "text-green-600" : "text-red-600"
									}`}
								>
									{saveStatus === "saving" ? (
										<>
											<svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
												<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
												<path
													className="opacity-75"
													fill="currentColor"
													d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
												/>
											</svg>
											Saving...
										</>
									) : saveStatus === "saved" ? (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
											</svg>
											Saved
										</>
									) : (
										<>
											<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
												<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
											</svg>
											Error saving
										</>
									)}
								</span>
							)}

							<Button type="button" onClick={handleDiscard} disabled={isLoading || isUploading || !hasChanges || saveStatus === "saving" || saveStatus === "saved"} variant="light">
								Discard
							</Button>

							<Button type="submit" disabled={isLoading || isUploading || !isValid || !hasChanges || saveStatus === "saving" || saveStatus === "saved"} variant="default">
								{isLoading ? "Updating..." : isUploading ? "Uploading..." : saveStatus === "saving" ? "Saving..." : "Save Changes"}
							</Button>
						</div>
					</div>
				</Container>
			</div>
		</form>
	);
}
